import React from "react";
import { Box, Typography, Tooltip, Divider } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { summaryCardTheme as t } from "./styles";

const formatCurrency = (value) =>
  typeof value === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    : "$0.00";

const formatCount = (value) =>
  typeof value === "number" && !Number.isNaN(value)
    ? new Intl.NumberFormat("en-US").format(value)
    : value;

const getTotalFailedControls = (overall) => {
  if (!overall || !Array.isArray(overall.compliance_by_framework)) return overall?.compliance_failed_controls_total ?? 0;
  return overall.compliance_by_framework.reduce(
    (sum, fw) => sum + (Number(fw.controls_failed_total) || 0),
    0
  );
};

/** Prefer breaks after "+" (ZWSP) so combined labels wrap cleanly. */
const formatKpiLabelDisplay = (label) =>
  typeof label === "string" ? label.replace(/\+/g, "+\u200b") : label;

const OVERLAP_THEMES = {
  arns_both: {
    bg: "rgba(59, 130, 246, 0.08)",
    color: "#2563eb",
  },
  arns_all_three: {
    bg: "rgba(202, 138, 4, 0.10)",
    color: "#ca8a04",
  },
  arns_all_four: {
    bg: "rgba(34, 139, 34, 0.08)",
    color: "#228B22",
  },
};

/** filterKey: stable id for ARN details table filtering (Summary page); omitted = not clickable */
const kpiConfig = (overall) => [
  {
    filterKey: "total_arns",
    label: "Total ARNs",
    value: overall?.arns_total ?? 0,
    tooltip: "Total number of ARNs scanned across all services",
  },
  {
    filterKey: "security",
    label: "Security",
    value: overall?.arns_security_failed ?? 0,
    tooltip: "ARNs with failed security checks (CSPM)",
    accent: "issue",
    compactAccent: "accent",
  },
  {
    filterKey: "compliance_controls",
    label: "Control ID",
    compactLabel: "Control IDs",
    value: getTotalFailedControls(overall),
    tooltip: "Total failed compliance controls across all frameworks",
  },
  {
    filterKey: "compliance_framework",
    label: "Compliance Framework",
    compactLabel: "Frameworks",
    value: overall?.compliance_frameworks_failed_count ?? null,
    tooltip: "Number of compliance frameworks with at least one failed control",
    emptyLabel: "—",
  },
  {
    filterKey: "cloud_waste",
    label: "Cloud Waste",
    value: overall?.arns_cloud_waste ?? 0,
    tooltip: "ARNs eligible for estimated monthly cost savings from Cloud Waste findings",
    /** Second row: total estimated monthly savings (USD) from summary API */
    secondaryLabel: "Est. Monthly Savings",
    secondaryValue: overall?.cloud_waste_savings_total,
    secondaryFormatter: formatCurrency,
    secondaryEmptyLabel: "—",
    accent: "success",
  },
  {
    filterKey: "cost",
    label: "Cost",
    value: overall?.cost_unblended_mtd_total ?? 0,
    formatter: formatCurrency,
    tooltip: "Unblended cost for the last 30 days across scanned ARNs",
    accent: "success",
  },
  {
    filterKey: "arns_both",
    label: "Security+Compliance",
    compactLabel: "Security + Compliance",
    value: overall?.arns_both ?? 0,
    tooltip: "ARNs with Security and Compliance findings",
    accent: "issue",
  },
  {
    filterKey: "arns_all_three",
    label: "Security+Compliance+Cost",
    compactLabel: "Security + Compliance + Cost",
    value: overall?.arns_all_three ?? 0,
    tooltip: "ARNs with Security, Compliance and Cost Insight findings",
    accent: "issue",
  },
  {
    filterKey: "arns_all_four",
    label: "Security+Compliance+Cost+Cloud Waste",
    compactLabel: "Security + Compliance + Cost + Cloud Waste",
    value: overall?.arns_all_four ?? 0,
    tooltip: "ARNs with Security, Compliance, Cost Insight and Cloud Waste findings",
    accent: "issue",
  },
];

const resolveAccentColor = (item, { compact = false } = {}) => {
  const isCurrencyPrimary = item.formatter === formatCurrency;
  if (isCurrencyPrimary) return t.colors.dollar;

  const accentKey = compact && item.compactAccent ? item.compactAccent : item.accent;
  if (accentKey === "accent") return t.colors.accent;
  if (accentKey === "issue") return t.colors.issue;
  if (accentKey === "warning") return t.colors.warning;
  if (accentKey === "success") return t.colors.success;
  return t.colors.text;
};

const buildDisplayValue = (item) => {
  const hasValue = item.value != null && item.value !== "" && (typeof item.value !== "number" || !Number.isNaN(item.value));
  if (item.emptyLabel != null && !hasValue) return item.emptyLabel;
  if (typeof item.formatter === "function") return item.formatter(item.value);
  if (typeof item.value === "number") return formatCount(item.value);
  return item.value;
};

const buildSecondaryDisplay = (item) => {
  const hasSecondary =
    item.secondaryLabel != null &&
    (item.secondaryValue != null || item.secondaryEmptyLabel != null);
  if (!hasSecondary) return { hasSecondary: false };

  const secondaryNum =
    item.secondaryValue != null && item.secondaryValue !== ""
      ? Number(item.secondaryValue)
      : NaN;
  const hasSecondaryNumber = Number.isFinite(secondaryNum);
  const displaySecondary =
    typeof item.secondaryFormatter === "function" && hasSecondaryNumber
      ? item.secondaryFormatter(secondaryNum)
      : hasSecondaryNumber
        ? String(secondaryNum)
        : item.secondaryEmptyLabel ?? "—";

  return { hasSecondary: true, displaySecondary };
};

const InfoTip = ({ item, tooltipContent, compact }) => (
  <Tooltip
    title={tooltipContent}
    arrow
    placement="top"
    enterDelay={200}
    leaveDelay={0}
    PopperProps={{
      modifiers: [
        {
          name: "preventOverflow",
          options: { boundary: "viewport", padding: 8 },
        },
        {
          name: "flip",
          options: { fallbackPlacements: ["top", "bottom", "right", "left"] },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "background.paper",
          color: "text.primary",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: 3,
          pointerEvents: "auto",
          maxWidth: "min(320px, 80vw)",
          whiteSpace: "normal",
          wordBreak: "break-word",
          maxHeight: "none",
          overflow: "hidden",
          overflowX: "hidden",
          "& .MuiTooltip-arrow": {
            color: "background.paper",
            "&::before": {
              border: "1px solid",
              borderColor: "divider",
            },
          },
        },
      },
    }}
  >
    <Box
      component="span"
      role="button"
      tabIndex={0}
      aria-label={`${item.label} info`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "text-top",
        ml: 0.35,
        mt: compact ? "1px" : 0,
        cursor: "pointer",
        color: t.colors.textMuted,
        lineHeight: 0,
        "&:hover": { color: t.colors.text },
      }}
    >
      <InfoOutlinedIcon fontSize="small" sx={{ fontSize: compact ? 14 : 16 }} />
    </Box>
  </Tooltip>
);

/** Shared compact typography — keep primary / overlap values consistent */
const COMPACT = {
  label: "0.6875rem",
  value: "0.9375rem",
  secondary: "0.625rem",
  sectionTitle: "0.6875rem",
};

/**
 * Side-by-side when this card is wide enough (container query on SummaryCard).
 * Uses card width — not viewport — so layout matches grid row height.
 * Narrower cards: Findings on top, Overlapping below (no inner scroll).
 */
const WIDE_UP = "@container arn-kpi (min-width: 900px)";

function CompactKpiLayout({ items }) {
  const primary = items.slice(0, 6);
  const overlapping = items.slice(6);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 0.35,
        width: "100%",
        minHeight: 0,
        flex: 1,
        height: "100%",
        justifyContent: "flex-start",
        [WIDE_UP]: {
          flexDirection: "row",
          gap: 0,
          alignItems: "stretch",
        },
      }}
    >
      {/* Findings (primary KPIs) */}
      <Box
        sx={{
          minWidth: 0,
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
          columnGap: { xs: 0.75, sm: 1 },
          rowGap: 0.25,
          alignContent: "center",
          alignItems: "center",
          flexShrink: 0,
          [WIDE_UP]: {
            flex: "0 1 auto",
            width: "auto",
            height: "100%",
            gridTemplateColumns: "repeat(6, max-content)",
            justifyContent: "start",
            columnGap: 1.75,
            alignContent: "center",
            pr: 1.25,
            pt: 0,
          },
        }}
      >
        {primary.map((item) => {
          const displayValue = buildDisplayValue(item);
          const color = resolveAccentColor(item, { compact: true });
          const { hasSecondary, displaySecondary } = buildSecondaryDisplay(item);
          const label = item.compactLabel || item.label;
          const tooltipContent = hasSecondary ? (
            <Box sx={{ p: 1, maxWidth: "min(320px, 80vw)" }}>
              <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "text.primary", lineHeight: 1.35 }}>
                {item.tooltip}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.85, lineHeight: 1.35 }}
              >
                <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                  {item.secondaryLabel}:
                </Box>{" "}
                {displaySecondary}
              </Typography>
            </Box>
          ) : (
            item.tooltip
          );

          return (
            <Box
              key={item.filterKey || item.label}
              sx={{
                minWidth: 0,
                [WIDE_UP]: { width: "max-content" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                  mb: 0.15,
                  minWidth: 0,
                  width: "100%",
                  [WIDE_UP]: { width: "max-content" },
                }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    color: t.colors.textMuted,
                    fontSize: COMPACT.label,
                    fontWeight: 600,
                    lineHeight: 1.25,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                    [WIDE_UP]: {
                      overflow: "visible",
                      textOverflow: "clip",
                    },
                  }}
                >
                  {label}
                </Typography>
                <Box sx={{ flexShrink: 0, display: "inline-flex" }}>
                  <InfoTip item={item} tooltipContent={tooltipContent} compact />
                </Box>
              </Box>

              <Typography
                variant="body2"
                component="div"
                sx={{
                  color,
                  fontWeight: 700,
                  fontSize: COMPACT.value,
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {displayValue}
              </Typography>

              <Typography
                component="div"
                sx={{
                  mt: 0.1,
                  fontSize: COMPACT.secondary,
                  fontWeight: 500,
                  color: t.colors.textMuted,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  /* Only take space when savings exist — avoids empty band under Findings */
                  ...(hasSecondary
                    ? {}
                    : { height: 0, mt: 0, overflow: "hidden", visibility: "hidden" }),
                }}
              >
                {hasSecondary ? (
                  <>
                    Est. mo.{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: t.colors.dollar }}>
                      {displaySecondary}
                    </Box>
                  </>
                ) : (
                  "\u00a0"
                )}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Divider
        orientation="vertical"
        flexItem
        sx={{
          display: "none",
          borderColor: t.colors.border,
          alignSelf: "stretch",
          [WIDE_UP]: { display: "block" },
        }}
      />
      <Divider
        sx={{
          borderColor: t.colors.border,
          my: 0.25,
          [WIDE_UP]: { display: "none" },
        }}
      />

      {/* Overlapping Findings (Observations) */}
      <Box
        sx={{
          minWidth: 0,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: 0.4,
          flexShrink: 0,
          [WIDE_UP]: {
            flex: "1 1 0",
            flexShrink: 1,
            width: "auto",
            height: "100%",
            minHeight: 0,
            pl: 1.25,
            pt: 0,
          },
        }}
      >
        <Box display="flex" alignItems="center" minWidth={0} sx={{ flexShrink: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: COMPACT.sectionTitle,
              color: t.colors.text,
              lineHeight: 1.2,
            }}
          >
            ARN - Multiple Findings
          </Typography>
          <Tooltip
            title="ARNs that appear across multiple finding categories"
            arrow
            placement="top"
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                ml: 0.35,
                color: t.colors.textMuted,
                cursor: "default",
                lineHeight: 0,
                "&:hover": { color: t.colors.text },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 0.5,
            minWidth: 0,
            width: "100%",
            [WIDE_UP]: {
              flex: "1 1 auto",
              minHeight: 0,
              alignItems: "stretch",
            },
          }}
        >
          {overlapping.map((item) => {
            const theme = OVERLAP_THEMES[item.filterKey] || {
              bg: t.colors.borderLight,
              color: t.colors.text,
            };
            const displayValue = buildDisplayValue(item);
            const label = formatKpiLabelDisplay(item.compactLabel || item.label);

            return (
              <Tooltip key={item.filterKey || item.label} title={item.tooltip} arrow placement="top">
                <Box
                  sx={{
                    backgroundColor: theme.bg,
                    borderRadius: t.radius.md,
                    px: 0.4,
                    py: 0.4,
                    minWidth: 0,
                    maxWidth: "100%",
                    height: "100%",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    textAlign: "center",
                    transition: "box-shadow 0.15s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontSize: COMPACT.secondary,
                      fontWeight: 600,
                      color: t.colors.textMuted,
                      lineHeight: 1.25,
                      mb: 0.25,
                      px: 0.1,
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      wordBreak: "normal",
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      fontWeight: 700,
                      fontSize: COMPACT.value,
                      fontVariantNumeric: "tabular-nums",
                      color: theme.color,
                      lineHeight: 1.15,
                    }}
                  >
                    {displayValue}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

function SummaryKpiCards({ overall, compact, activeFilterKey, onFilterClick, emphasizeSelection = false }) {
  const items = kpiConfig(overall);
  const clickable = typeof onFilterClick === "function";

  if (compact) {
    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minHeight: 0,
          flex: 1,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CompactKpiLayout items={items} />
      </Box>
    );
  }

  const gridSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(2, minmax(0, 1fr))",
      sm: "repeat(3, minmax(0, 1fr))",
      md: "repeat(4, minmax(0, 1fr))",
      lg: "repeat(4, minmax(0, 1fr))",
      xl: "repeat(5, minmax(0, 1fr))",
    },
    gap: 2,
    minWidth: 0,
  };

  return (
    <Box sx={gridSx}>
      {items.map((item) => {
        const displayValue = buildDisplayValue(item);
        const color = resolveAccentColor(item);
        const { hasSecondary, displaySecondary } = buildSecondaryDisplay(item);

        const tooltipContent = hasSecondary ? (
          <Box sx={{ p: 1, maxWidth: "min(320px, 80vw)" }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.78rem", color: "text.primary", lineHeight: 1.35 }}
            >
              {item.tooltip}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                mt: 0.85,
                lineHeight: 1.35,
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                {item.secondaryLabel}:
              </Box>{" "}
              {displaySecondary}
            </Typography>
          </Box>
        ) : (
          item.tooltip
        );

        const isActive = clickable && item.filterKey && activeFilterKey === item.filterKey;
        const isClickableCard = clickable && item.filterKey;
        const fadeKpiPeer =
          emphasizeSelection && activeFilterKey && !isActive;

        return (
          <Box
            key={item.label}
            onClick={
              isClickableCard
                ? () => onFilterClick(item.filterKey)
                : undefined
            }
            role={isClickableCard ? "button" : undefined}
            tabIndex={isClickableCard ? 0 : undefined}
            onKeyDown={
              isClickableCard
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onFilterClick(item.filterKey);
                    }
                  }
                : undefined
            }
            sx={{
              position: "relative",
              zIndex: emphasizeSelection && isActive ? 1 : 0,
              backgroundColor: t.colors.card,
              border: `1px solid ${isActive ? t.colors.accent : t.colors.border}`,
              borderRadius: t.radius.lg,
              p: 2,
              minWidth: 0,
              boxShadow:
                emphasizeSelection && isActive
                  ? `0 0 0 1px ${t.colors.accent}, 0 8px 22px rgba(59, 130, 246, 0.2)`
                  : t.shadow,
              transition: "border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.2s ease, transform 0.2s ease",
              cursor: isClickableCard ? "pointer" : "default",
              outline: isActive ? `2px solid ${t.colors.accent}` : "none",
              outlineOffset: isActive ? 1 : 0,
              opacity: fadeKpiPeer ? 0.5 : 1,
              transform: emphasizeSelection && isActive ? "scale(1.02)" : "none",
              "&:hover": {
                borderColor: isActive ? t.colors.accent : t.colors.borderLight,
                boxShadow:
                  emphasizeSelection && isActive
                    ? `0 0 0 1px ${t.colors.accent}, 0 10px 26px rgba(59, 130, 246, 0.24)`
                    : "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Box
              sx={{
                mb: 0.5,
                minWidth: 0,
                width: "100%",
              }}
            >
              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: t.colors.text,
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  lineHeight: 1.25,
                }}
              >
                {formatKpiLabelDisplay(item.label)}
              </Typography>
              <InfoTip item={item} tooltipContent={tooltipContent} compact={false} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  color,
                  fontWeight: "bold",
                  fontSize: "1rem",
                  fontVariantNumeric: "tabular-nums",
                  lineHeight: 1.2,
                }}
              >
                {displayValue}
              </Typography>
              {hasSecondary && (
                <Box
                  sx={{
                    mt: 0.75,
                    pt: 0.65,
                    borderTop: `1px solid ${t.colors.borderLight}`,
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "0.625rem",
                      fontWeight: "bold",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: t.colors.textMuted,
                      lineHeight: 1.2,
                      mb: 0.15,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.secondaryLabel}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "0.9375rem",
                      fontWeight: "bold",
                      fontVariantNumeric: "tabular-nums",
                      color: t.colors.dollar,
                      lineHeight: 1.2,
                    }}
                  >
                    {displaySecondary}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default SummaryKpiCards;
